import React, { useEffect } from 'react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Upload } from 'src/components/upload';
import Iconify from 'src/components/iconify';
import { CaySamNhatKy } from 'src/types/caysam';
import { RHFSwitch } from 'src/components/hook-form';
import { getImageUrlWithCacheBuster } from 'src/utils/image-timestap';
import CustomLoadingButton from 'src/components/button/button-loadding-dark-model';
import { OptionModel } from 'src/types/api-response';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<CaySamNhatKy>) => Promise<void>;
  nhatKyData: Partial<CaySamNhatKy> | null;
  tinhTrangOptions: OptionModel[];
  diemSucKhoeOptions: OptionModel[];
  loading?: boolean;
};

export default function NhatKyFormDialog({
  open,
  onClose,
  onSubmit,
  nhatKyData,
  tinhTrangOptions,
  diemSucKhoeOptions,
  loading = false,
}: Props) {
  
  // 1. Logic tính toán giá trị mặc định (Bao gồm cả việc chọn option đầu tiên)
  const defaultValues = React.useMemo<Partial<CaySamNhatKy>>(() => {
    const initialValues = {
      tinhTrang: 1,
      diemSucKhoe: 0,
      soLa: 0,
      trongLuong: 0,
      hinhAnhTongQuan: '',
      hinhAnhChiTiet: '',
      ngayGhi: new Date().toISOString(),
      isThongBao: false,
      ghiChu: '',
    };

    const mergedData = { ...initialValues, ...nhatKyData };

    // FIX: Nếu chưa có điểm sức khỏe (hoặc = 0) VÀ có danh sách options
    // Thì lấy giá trị đầu tiên trong danh sách gán vào luôn.
    if (!mergedData.diemSucKhoe && diemSucKhoeOptions && diemSucKhoeOptions.length > 0) {
       mergedData.diemSucKhoe = Number(diemSucKhoeOptions[0].value);
    }

    if (mergedData.hinhAnhTongQuan) {
      mergedData.hinhAnhTongQuan = getImageUrlWithCacheBuster(mergedData.hinhAnhTongQuan) || '';
    }
    if (mergedData.hinhAnhChiTiet) {
      mergedData.hinhAnhChiTiet = getImageUrlWithCacheBuster(mergedData.hinhAnhChiTiet) || '';
    }

    return mergedData;
  }, [nhatKyData, diemSucKhoeOptions]); // Thêm diemSucKhoeOptions vào dependency

  const methods = useForm<Partial<CaySamNhatKy>>({
    defaultValues,
  });

  const { control, setValue, handleSubmit, watch, reset } = methods;

  const formData = watch();

  // 2. Reset form mỗi khi defaultValues thay đổi (bao gồm khi options load xong)
  useEffect(() => {
    if (open) {
       reset(defaultValues);
    }
  }, [open, reset, defaultValues]);

  const onFormSubmit = async (data: Partial<CaySamNhatKy>) => {
    await onSubmit(data);
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogTitle>
            {nhatKyData?.id ? 'Cập nhật nhật ký sinh trưởng' : 'Thêm nhật ký sinh trưởng'}
          </DialogTitle>

          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Stack direction="row" spacing={2}>
                <Upload
                  helperTitle="Ảnh chụp tổng thể"
                  file={formData.hinhAnhTongQuan || ''}
                  onDrop={(acceptedFiles) => {
                    const file = acceptedFiles[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = () => {
                        setValue('hinhAnhTongQuan', reader.result as string, {
                          shouldValidate: true,
                        });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  onDelete={() => setValue('hinhAnhTongQuan', '', { shouldValidate: true })}
                  sx={{ flex: 1 }}
                />

                <Upload
                  helperTitle="Ảnh chụp cận cảnh"
                  file={formData.hinhAnhChiTiet || ''}
                  onDrop={(acceptedFiles) => {
                    const file = acceptedFiles[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = () => {
                        setValue('hinhAnhChiTiet', reader.result as string, {
                          shouldValidate: true,
                        });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  onDelete={() => setValue('hinhAnhChiTiet', '', { shouldValidate: true })}
                  sx={{ flex: 1 }}
                />
              </Stack>

              <Controller
                name="tinhTrang"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Tình trạng</InputLabel>
                    <Select
                      {...field}
                      label="Tình trạng"
                      startAdornment={<Iconify icon="solar:flag-2-bold" sx={{ mr: 1 }} />}
                    >
                      {tinhTrangOptions.map((opt) => (
                        <MenuItem key={opt.value} value={Number(opt.value)}>
                          {opt.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="diemSucKhoe"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Điểm sức khỏe</InputLabel>
                    <Select
                      {...field}
                      label="Điểm sức khỏe"
                      startAdornment={<Iconify icon="solar:check-circle-bold" sx={{ mr: 1 }} />}
                    >
                      {diemSucKhoeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={Number(opt.value)}>
                          {opt.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="soLa"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Số lá"
                    size="small"
                    type="number"
                    InputProps={{
                      startAdornment: <Iconify icon="solar:leaf-bold" sx={{ mr: 1 }} />,
                    }}
                  />
                )}
              />
              <Controller
                name="trongLuong"
                control={control}
                render={({ field }) => (
                  <TextField 
                    {...field}
                    fullWidth
                    label="Trọng lượng"
                    size="small"
                    type="number"
                    InputProps={{
                      startAdornment: <Iconify icon="solar:leaf-bold" sx={{ mr: 1 }} />,
                    }}
                  />
                )}
              />
              <Controller
                name="ghiChu"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Ghi chú"
                    size="small"
                    type="text"
                    InputProps={{
                      startAdornment: <Iconify icon="solar:document-text-bold" sx={{ mr: 1 }} />,
                    }}
                  />
                )}
              />
              <FormControlLabel
                control={<RHFSwitch name="isThongBao" label="" />}
                label="Gửi thông báo"
                sx={{ ml: 0, justifyContent: 'flex-start' }}
              />
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose} variant="outlined">
              Hủy
            </Button>
            <CustomLoadingButton type="submit" variant="contained" loading={loading}>
              {nhatKyData?.id ? 'Cập nhật' : 'Thêm mới'}
            </CustomLoadingButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}